import React, { useEffect, useState } from "react";
import { useTranslation } from "context/TranslationProvider";
import userServices from "services/userServices";
import classnames from "classnames";
import PropTypes from "prop-types";
import { useAuth } from "context/AuthContext";
import { useNavigate } from 'react-router-dom';
import NotificationList from "views/pages/notification/NotificationList";
import MessageList from "views/pages/messages/MessagesList";
import helpers from "utils/helpers";
import {
  Collapse,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  DropdownToggle,
  Form,
  Media,
  Navbar,
  NavItem,
  Nav,
  Container,
  Spinner,
} from "reactstrap";
import constants from "constants";

function AdminNavbar({ theme, sidenavOpen, toggleSidenav }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t, language, changeLanguage } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const selectedLang = languages.find((l) => l.code === language);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [userMessages, setUserMessages] = useState([]);
  const [messageHoveredId, setMessageHoveredId] = useState(null);

  const unreadMessageCount = userMessages.reduce(
    (count, message) => count + (message.unreadCount || 0),
    0
  );

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await userServices.fetchOrderConversation({
          conversation_type: helpers.NOTIFICATION_FILTERS.ALL,
        });
        const convs = Array.isArray(res?.data?.conversations)
          ? res.data.conversations
          : Array.isArray(res?.conversations)
          ? res.conversations
          : [];
        setUserMessages(convs);
      } catch (err) {
        console.error("Error fetching messages:", err);
      }
    };

    fetchMessages();
    const intervalMsg = setInterval(fetchMessages, 15000);

    const handleMessagesRead = () => {
      fetchMessages();
    };

    window.addEventListener("messagesRead", handleMessagesRead);

    return () => {
      clearInterval(intervalMsg);
      window.removeEventListener("messagesRead", handleMessagesRead);
    };
  }, []);

  const messageMarkAsRead = async (conversationId, conversationType, channel) => {
    const payload = {
      type: "markAllAsRead",
      conversationId,
      conversationType,
      channel,
    };

    try {
      await userServices.updateConversationMessage(payload);
      setUserMessages((prevConversations) =>
        prevConversations.map((conversation) =>
          conversation.reference_id === conversationId
            ? { ...conversation, unreadCount: 0 }
            : conversation
        )
      );
    } catch (error) {
      console.error("Error marking conversation as read:", error);
    }
  };

  useEffect(() => {
    const fetchUserNotifications = async () => {
      try {
        const res = await userServices.fetchNotifications();
        const notifs = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.notifications)
          ? res.notifications
          : [];
        setNotifications(notifs);
        const serverUnread = res?.pagination?.unreadCount;
        setUnreadCount(typeof serverUnread === "number" ? serverUnread : notifs.filter(n => !n.read).length);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      } finally {
        setNotifLoading(false);
      }
    };

    fetchUserNotifications();
    const intervalNotif = setInterval(fetchUserNotifications, 15000);

    window.addEventListener("notificationsRead", fetchUserNotifications);
    return () => {
      clearInterval(intervalNotif);
      window.removeEventListener("notificationsRead", fetchUserNotifications);
    };
  }, [navigate]);

  const markAsRead = async (notificationId) => {
    try {
      const res = await userServices.updateNotification(notificationId, { read: true });
      const isSuccess = res?.success || (!res?.error && res);
      if (isSuccess) {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) =>
            notification._id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        window.dispatchEvent(new Event("notificationsRead"));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const res = await userServices.getLanguages();
        const langs = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setLanguages(langs);
      } catch (err) {
        console.error("Failed to fetch languages", err);
      }
    };

    fetchLanguages();
  }, []);

  const handleLogout = () => {
    logout();
  };
  // function that on mobile devices makes the search close
  const closeSearch = () => {
    document.body.classList.remove("g-navbar-search-shown");
    setTimeout(function () {
      document.body.classList.remove("g-navbar-search-show");
      document.body.classList.add("g-navbar-search-hiding");
    }, 150);
    setTimeout(function () {
      document.body.classList.remove("g-navbar-search-hiding");
      document.body.classList.add("g-navbar-search-hidden");
    }, 300);
    setTimeout(function () {
      document.body.classList.remove("g-navbar-search-hidden");
    }, 500);
  };

  const profilePicUrl =
    user?.profilePictureUrl
      ? `${constants.s3Bucket}/profile-pics/${user.profilePictureUrl}`
      : null;

  return (
    <>
      <Navbar
        className={classnames(
          "navbar-top navbar-expand border-bottom",
          { "navbar-dark bg-orange": theme === "dark" },
          { "navbar-light bg-secondary": theme === "light" }
        )}
      >
        <Container fluid>
          <Collapse navbar isOpen={true}>
            <Form
              className={classnames(
                "navbar-search form-inline mr-sm-3",
                { "navbar-search-light": theme === "dark" },
                { "navbar-search-dark": theme === "light" }
              )}
            >
              <button
                aria-label="Close"
                className="close"
                type="button"
                onClick={closeSearch}
              >
                <span aria-hidden={true}>×</span>
              </button>
            </Form>

            <Nav className="align-items-center ml-md-auto" navbar>
              <NavItem className="d-xl-none">
                <div
                  className={classnames(
                    "pr-3 sidenav-toggler",
                    { active: sidenavOpen },
                    { "sidenav-toggler-dark": theme === "dark" }
                  )}
                  onClick={toggleSidenav}
                >
                  <div className="sidenav-toggler-inner">
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                    <i className="sidenav-toggler-line" />
                  </div>
                </div>
              </NavItem>
            </Nav>
            <Nav className="align-items-center ml-auto ml-md-0" navbar>
              <UncontrolledDropdown nav>
                <DropdownToggle className="nav-link position-relative" color="" tag="a">
                  <i className="ni ni-bell-55" />
                  {unreadCount > 0 && (
                    <span className="notification-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-xl py-0 overflow-hidden" right style={{ zIndex: 1100 }}>
                  <div className="px-3 py-3">
                    <h6 className="text-sm text-muted m-0">
                      {t("page.notifications.youHave", "You have")}{" "}
                      <strong className="text-info">{unreadCount}</strong>{" "}
                      {t("page.notifications.notifications", "new notifications")}
                    </h6>
                  </div>
                  {notifLoading && notifications.length === 0 ? (
                    <div className="d-flex flex-column justify-content-center align-items-center py-4">
                      <Spinner size="sm" color="primary" />
                    </div>
                  ) : notifications.length > 0 ? (
                    <NotificationList
                      notifications={[...notifications].sort(
                        (firstNotification, secondNotification) =>
                          new Date(secondNotification.createdAt) - new Date(firstNotification.createdAt)
                      )}
                      markAsRead={markAsRead}
                      navigate={navigate}
                      hoveredNotificationId={hoveredId}
                      setHoveredNotificationId={setHoveredId}
                      t={t}
                      maxItems={5}
                    />
                  ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center py-6">
                      <span className="text-sm md:text-base">
                        {t("page.notifications.noNotifications", "No Notifications Yet")}
                      </span>
                    </div>
                  )}
                  <DropdownItem
                    className="text-center text-info font-weight-bold py-3"
                    onClick={() => navigate("/admin/notification")}
                  >
                    {t("page.notifications.viewAll", "View all")}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <UncontrolledDropdown className="mr-3" nav>
                <DropdownToggle
                  className="nav-link position-relative"
                  color=""
                  tag="a"
                >
                  <i className="ni ni-chat-round"/>

                  {unreadMessageCount > 0 && (
                    <span className="notification-dot"></span>
                  )}
                </DropdownToggle>

                <DropdownMenu
                  className="dropdown-menu-xl py-0 overflow-hidden"
                  right
                >
                  <div className="px-3 py-3">
                    <h6 className="text-sm text-muted m-0">
                      {t("page.notifications.youHave", "You have")}{" "}
                      <strong className="text-info">
                        {unreadMessageCount}
                      </strong>{" "}
                      {t("page.messages.messages", "messages")}
                    </h6>
                  </div>
                  {userMessages.length > 0 ? (
                    <MessageList
                      messages = {[...userMessages].sort(
                        (firstMessage, secondMessage) =>
                          new Date(secondMessage.updatedAt) - new Date(firstMessage.updatedAt)
                      )}
                      markAsRead={messageMarkAsRead}
                      navigate={navigate}
                      hoveredConversationId={messageHoveredId}
                      setHoveredConversationId={setMessageHoveredId}
                      t={t}
                      maxItems={5}
                    />
                  ) : (
                    <div className="d-flex flex-column justify-content-center align-items-center py-6">
                      <span className="text-sm md:text-base">
                        {t("page.messages.noConversation", "No Conversations Yet")}
                      </span>
                    </div>
                  )}
                  <DropdownItem
                    className="text-center text-info font-weight-bold py-3"
                    onClick={() => navigate("/admin/messages")}
                  >
                    {t("page.notifications.viewAll", "View all")}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <UncontrolledDropdown className="mr-1" nav>
                <DropdownToggle
                  className="btn btn-sm btn-neutral d-flex align-items-center"
                  caret
                >
                  {selectedLang && (
                    <>
                      <img
                        src={`https://flagcdn.com/w20/${selectedLang.flagCode}.png`}
                        alt={selectedLang.name}
                        className="mr-2 rounded-sm"
                      />
                      <span className="text-uppercase font-weight-bold">
                        {selectedLang.code}
                      </span>
                    </>
                  )}
                </DropdownToggle>

                <DropdownMenu right className="dropdown-menu-arrow">
                  {languages.map((lang) => (
                    <DropdownItem
                      key={lang.code}
                      disabled={!lang.synced}
                      onClick={() => changeLanguage(lang.code)}
                      className="d-flex align-items-center"
                    >
                      <img
                        src={`https://flagcdn.com/w20/${lang.flagCode}.png`}
                        alt={lang.name}
                        className="mr-2 rounded-sm"
                      />
                      <span>{lang.name}</span>

                      {lang.code === language && (
                        <span className="ml-auto text-muted">✓</span>
                      )}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
              <UncontrolledDropdown nav>
                <DropdownToggle className="nav-link pr-0" color="" tag="a">
                  <Media className="align-items-center">
                    {profilePicUrl ? (
                      <img
                        alt="Profile"
                        src={profilePicUrl}
                        className="avatar avatar-sm rounded-circle"
                      />
                    ) : (
                      <span className="avatar avatar-sm rounded-circle d-flex justify-content-center align-items-center bg-primary text-white">
                        {user?.firstName?.trim().charAt(0).toUpperCase()}
                      </span>
                    )}
                    <Media className="ml-2 d-none d-lg-block">
                      <span className="mb-0 text-sm font-weight-bold">
                        {user?.firstName || "Admin"}
                      </span>
                    </Media>
                  </Media>
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem className="noti-title" header tag="div">
                    <h6 className="text-overflow m-0">
                      {t("auth.login.welcome", "Welcome!")}
                    </h6>
                  </DropdownItem>
                  <DropdownItem onClick={() => navigate("/admin/profile")}>
                    <i className="ni ni-single-02" />
                    <span>{t("auth.login.myProfile", "My Profile")}</span>
                  </DropdownItem>
                  <DropdownItem onClick={() => navigate("/admin/setting")}>
                    <i className="ni ni-settings-gear-65" />
                    <span>{t("auth.login.setting", "Setting")}</span>
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleLogout}>
                    <i className="ni ni-user-run" />
                    <span>{t("auth.login.logout", "Logout")}</span>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
    </>
  );
}

AdminNavbar.defaultProps = {
  toggleSidenav: () => { },
  sidenavOpen: false,
  theme: "dark",
};
AdminNavbar.propTypes = {
  toggleSidenav: PropTypes.func,
  sidenavOpen: PropTypes.bool,
  theme: PropTypes.oneOf(["dark", "light"]),
};

export default AdminNavbar;
